// src/components/models/deleteModal.jsx
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBackToClose } from "../../utils/backToClose";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1 },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.18, ease: "easeOut" } },
};

export default function DeleteModal({
  show = false,
  handleClose = () => {},
  handleConfirm = () => {},
  title = "Confirm Delete",
  message = "Are you sure you want to delete this item?",
  confirmText = "Delete",
  confirmingText,
  cancelText = "Cancel",
  confirmButtonClassName = "bg-red-600 hover:bg-red-700",
  loading = false,
}) {
  useBackToClose(show, handleClose);
  const modalRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && show) handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show, handleClose]);

  // Basic focus management: focus modal on open
  useEffect(() => {
    if (show && modalRef.current) {
      const timer = setTimeout(() => modalRef.current.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial="hidden"
          animate="show"
          exit="hidden"
          variants={backdrop}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 dark:bg-black/60"
            onClick={!loading ? handleClose : undefined}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            tabIndex={-1}
            ref={modalRef}
            className="relative mx-4 w-full max-w-md rounded-xl bg-app-card shadow-2xl border border-app-primary focus:outline-none dark:shadow-black/50"
            variants={modal}
          >
            <div className="p-6">
              <h3
                id="delete-modal-title"
                className="text-xl font-semibold text-app-heading mb-2"
              >
                {title}
              </h3>
              <p className="text-app-secondary">{message}</p>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-app-primary px-6 py-4 bg-app-secondary rounded-b-xl dark:bg-slate-900/60">
              <button
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 rounded-lg border border-app-primary bg-app-card text-app-secondary transition-colors hover:bg-gray-100 hover:text-app-primary disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-slate-700"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`px-4 py-2 rounded-lg text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${confirmButtonClassName}`}
              >
                {loading ? (confirmingText || `${confirmText}...`) : confirmText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
